import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { useSort } from '../lib/sort';
import { PageHeader, Card } from '../components/ui';
import { Plus, Eye, Mail, Check, Trash2, RotateCcw, Search } from 'lucide-react';

const StatusBadge = ({ s }) => {
  const map = { betalt: 'badge-success', sendt: 'badge-purple', usendt: 'badge-neutral', kreditnota: 'badge-warning' };
  return <span className={`badge ${map[s] || 'badge-neutral'}`}>{s}</span>;
};

const Faktura = () => {
  const [liste, setListe] = useState(null);
  const [sok, setSok] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const nav = useNavigate();

  const last = () => api.get('/faktura').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);

  const settStatus = async (f, status) => {
    await api.post(`/faktura/${f.id}/status`, { status });
    last();
  };
  const slett = async (f) => {
    if (!window.confirm(`Slett faktura ${f.fakturanummer}?`)) return;
    try { await api.delete(`/faktura/${f.id}`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };
  const krediter = async (f) => {
    if (!window.confirm(`Lag kreditnota for ${f.fakturanummer}?`)) return;
    try { await api.post(`/faktura/${f.id}/kreditnota`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };

  const filtrert = (liste || []).filter((f) => {
    if (statusFilter !== 'alle' && (f.status || '').toLowerCase() !== statusFilter) return false;
    if (!sok.trim()) return true;
    const q = sok.toLowerCase();
    return [f.fakturanummer, f.kunde_navn]
      .some((v) => (v || '').toString().toLowerCase().includes(q));
  });

  const sortAcc = {
    fakturanummer: (f) => f.fakturanummer || '',
    kunde: (f) => f.kunde_navn || '',
    dato: (f) => f.dato,
    forfall: (f) => f.forfall,
    status: (f) => f.status || '',
    eks: (f) => Number(f.sum_eks_mva || 0),
    mva: (f) => Number(f.sum_mva || 0),
    ink: (f) => Number(f.sum_ink_mva || 0),
  };
  const { sorted: rader, Th } = useSort(filtrert, { defaultKey: 'dato', defaultDir: 'desc', accessors: sortAcc });

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader
        title="Fakturaer"
        actions={<button className="btn-primary" onClick={() => nav('/faktura/ny')}><Plus size={14} /> Ny faktura</button>}
      />
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 220 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%',
                                       transform: 'translateY(-50%)', opacity: 0.55 }} />
            <input value={sok} onChange={(e) => setSok(e.target.value)}
                   placeholder="Søk fakturanr, kunde…"
                   style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <label style={{ margin: 0 }}>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 180 }}>
            <option value="alle">Alle</option>
            <option value="utkast">Utkast</option>
            <option value="sendt">Sendt</option>
            <option value="betalt">Betalt</option>
            <option value="forfalt">Forfalt</option>
            <option value="kreditnota">Kreditnota</option>
          </select>
        </div>
      </Card>
      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              {Th('fakturanummer', 'Faktura#')}
              {Th('kunde', 'Kunde')}
              {Th('dato', 'Dato')}
              {Th('forfall', 'Forfall')}
              {Th('status', 'Status')}
              {Th('eks', 'Eks. MVA', { align: 'right' })}
              {Th('mva', 'MVA', { align: 'right' })}
              {Th('ink', 'Ink. MVA', { align: 'right' })}
              <th style={{ width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {rader.length === 0 && (
              <tr><td colSpan={9} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>Ingen fakturaer enda.</td></tr>
            )}
            {rader.map((f) => (
              <tr key={f.id}>
                <td><Link to={`/faktura/${f.id}`} style={{ color: 'var(--theme-accent)', textDecoration: 'none' }}>{f.fakturanummer}</Link></td>
                <td>{f.kunde_navn}</td>
                <td>{dato(f.dato)}</td>
                <td>{dato(f.forfall)}</td>
                <td><StatusBadge s={f.status} /></td>
                <td style={{ textAlign: 'right' }}>{kr(f.sum_eks_mva)}</td>
                <td style={{ textAlign: 'right' }}>{kr(f.sum_mva)}</td>
                <td style={{ textAlign: 'right' }}>{kr(f.sum_ink_mva)}</td>
                <td>
                  <div className="flex" style={{ gap: 4 }}>
                    <Link to={`/faktura/${f.id}`} className="btn-ghost" title="Vis"><Eye size={12} /></Link>
                    <button className="btn-ghost" onClick={() => settStatus(f, 'sendt')} title="Merk sendt"><Mail size={12} /></button>
                    <button className="btn-ghost" onClick={() => settStatus(f, 'betalt')} title="Merk betalt" style={{ color: 'var(--theme-success)' }}><Check size={12} /></button>
                    <button className="btn-ghost" onClick={() => krediter(f)} title="Kreditnota" style={{ color: 'var(--theme-warning)' }} disabled={f.kreditnota}><RotateCcw size={12} /></button>
                    <button className="btn-ghost" onClick={() => slett(f)} title="Slett" style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
};

export default Faktura;
